import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Box, Switch, FormControlLabel } from '@mui/material';
import NumericPad from '../NumericPad';
import ActivityLayout from '../ActivityLayout';

function CountObjects({ onBack, onCorrect, initialTimer = null, globalTimer = null, onGlobalTimeUp, score }) {
  const objects = ['⚽', '🚗', '🐊', '🐶', '🏠', '🌸', '🚲', '🦖', '🐟', '📚', '🧸', '🐒'];

  const [previousObjectCount, setPreviousObjectCount] = useState(null);
  const [previousObjectType, setPreviousObjectType] = useState(null);
  const [repeatedCount, setRepeatedCount] = useState(0);
  const [dominoMode, setDominoMode] = useState(true);
  const [emoji, setEmoji] = useState(null);
  const [showNext, setShowNext] = useState(false);

  const [objectCount, setObjectCount] = useState(0);
  const [objectType, setObjectType] = useState('');

  useEffect(() => {
    generateNewObject();
  }, []);

  function generateObjectCount() {
    let newCount = Math.ceil(Math.random() * 10);
    if (newCount === previousObjectCount && repeatedCount >= 3) {
      while (newCount === previousObjectCount) {
        newCount = Math.ceil(Math.random() * 10);
      }
      setRepeatedCount(1);
    } else if (newCount === previousObjectCount) {
      setRepeatedCount(repeatedCount + 1);
    } else {
      setRepeatedCount(1);
    }
    return newCount;
  }

  function generateObjectType() {
    let newType = objects[Math.floor(Math.random() * objects.length)];
    while (newType === previousObjectType) {
      newType = objects[Math.floor(Math.random() * objects.length)];
    }
    return newType;
  }

  function generateNewObject() {
    const newCount = generateObjectCount();
    const newType = generateObjectType();

    setPreviousObjectCount(objectCount);
    setPreviousObjectType(objectType);
    setObjectCount(newCount);
    setObjectType(newType);
    setEmoji(null);
    setShowNext(false);
  }

  const handleAnswer = (answer) => {
    if (showNext) return;
    
    const correct = answer === objectCount;
    setEmoji(correct ? '🎉' : '❌');
    setShowNext(true);
    
    if (correct) {
      onCorrect();
    }
  };

  function getDominoLayout(count) {
    const layouts = {
      2: [
        [null, objectType, null],
        [null, null, null],
        [null, , objectType, null,],
      ], 3: [
        [objectType, null, null],
        [null, objectType, null],
        [null, , null, objectType],
      ],
      4: [
        [objectType, null, objectType],
        [null, null, null],
        [objectType, null, objectType],
      ],
      5: [
        [objectType, null, objectType],
        [null, objectType, null],
        [objectType, null, objectType],
      ],
    };
    return layouts[count] || null;
  }

  function groupObjects(count) {
    if (count <= 2) return [count];

    const groups = [];
    let remaining = count;
    while (remaining > 0) {
      const groupSize = Math.min(remaining, Math.floor(Math.random() * 3) + 3);
      groups.push(groupSize);
      remaining -= groupSize;
    }
    return groups;
  }

  // Memoize the groups so they don't change when answering
  const groupedCounts = useMemo(() => groupObjects(objectCount), [objectCount]);

  return (
    <ActivityLayout 
      onBack={onBack}
      timeLimit={initialTimer}
      globalTimeLimit={globalTimer}
      onTimeUp={() => {
        setEmoji('⏰');
        setShowNext(true);
      }}
      onGlobalTimeUp={onGlobalTimeUp}
      isPaused={showNext}
      score={score}
    >
      <Box
        onClick={showNext ? generateNewObject : undefined}
        sx={{
          cursor: showNext ? 'pointer' : 'default',
          textAlign: 'center',
          mt: 3,
        }}
      >
        <Typography variant="h6">Combien y a-t-il de {objectType} ?</Typography>

        <Box display="flex" justifyContent="center" mb={2}>
          <FormControlLabel
            control={<Switch checked={dominoMode} onChange={() => setDominoMode(!dominoMode)} />}
            label="Domino mode"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            my: 2,
          }}
        >
          {dominoMode
            ? groupedCounts.map((groupSize, index) => {
              const layout = getDominoLayout(groupSize);
              return layout ? (
                <Box
                  key={index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '2px',
                    border: '2px solid blue',
                    p: 1,
                    borderRadius: '10px',
                    minWidth: '30px',
                  }}
                >
                  {layout.flat().map((cell, i) => (
                    <Typography key={i} variant="h6" sx={{ fontSize: '2rem', textAlign: 'center' }}>
                      {cell || ' '}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    border: '2px solid red',
                    p: 1,
                    borderRadius: '10px',
                    minWidth: '30px',
                  }}
                >
                  {Array.from({ length: groupSize }).map((_, i) => (
                    <Typography key={i} variant="h6" sx={{ fontSize: '2rem', mx: '3px' }}>
                      {objectType}
                    </Typography>
                  ))}
                </Box>
              );
            })
            : Array.from({ length: objectCount }).map((_, i) => (
              <Typography key={i} variant="h6" sx={{ fontSize: '2rem', mx: '3px' }}>
                {objectType}
              </Typography>
            ))}
        </Box>
        {/* Numeric Pad Container with Overlay */}
        <NumericPad onClick={handleAnswer} disabled={showNext} emoji={emoji} />
      </Box>
    </ActivityLayout>
  );
}

export default CountObjects;
